import { PartsStyleObject } from '@/util/chakra';
import { tableAnatomy as parts } from '@chakra-ui/anatomy';
import {
    createMultiStyleConfigHelpers,
    defineStyle,
} from '@chakra-ui/styled-system';
import { mode } from '@chakra-ui/theme-tools';

const { defineMultiStyleConfig, definePartsStyle } =
    createMultiStyleConfigHelpers(parts.keys);

const baseStyle = definePartsStyle({
    table: {
        fontVariantNumeric: 'lining-nums tabular-nums',
        borderCollapse: 'collapse',
        width: 'full',
    },
    th: {
        fontFamily: 'heading',
        fontWeight: 'normal',
        textTransform: 'uppercase',
        letterSpacing: 'wider',
        textAlign: 'start',
    },
    td: {
        textAlign: 'start',
    },
    caption: {
        mt: 4,
        fontFamily: 'heading',
        textAlign: 'center',
        fontWeight: 'medium',
    },
});

const numericStyles = defineStyle({
    '&[data-is-numeric=true]': {
        textAlign: 'end',
    },
});

const variantSimple = definePartsStyle((props) => {
    const { colorScheme: c } = props;

    return {
        th: {
            color: mode('gray.600', 'gray.400')(props),
            borderBottom: '1px',
            borderColor: mode(`${c}.300`, `${c}.600`)(props),
            ...numericStyles,
        },
        td: {
            borderBottom: '1px',
            borderColor: mode(`${c}.300`, `${c}.600`)(props),
            ...numericStyles,
        },
        caption: {
            color: mode('gray.600', 'gray.100')(props),
        },
        tr: {
            '&:last-of-type': {
                td: { borderBottomWidth: 0 },
            },
        },
    };
});

const variantStripe = definePartsStyle((props) => {
    const { colorScheme: c } = props;

    return {
        th: {
            color: mode('gray.600', 'gray.400')(props),
            borderBottom: '1px',
            borderColor: mode(`${c}.300`, `${c}.600`)(props),
            ...numericStyles,
        },
        td: {
            borderBottom: '1px',
            borderColor: mode(`${c}.300`, `${c}.600`)(props),
            ...numericStyles,
        },
        caption: {
            color: mode('gray.600', 'gray.100')(props),
        },
        tbody: {
            tr: {
                '&:nth-of-type(odd)': {
                    'th, td': {
                        borderBottomWidth: '1px',
                        borderColor: mode(`${c}.300`, `${c}.600`)(props),
                    },
                    td: {
                        background: mode(`${c}.200`, `${c}.600`)(props),
                    },
                },
            },
        },
        tfoot: {
            tr: {
                '&:last-of-type': {
                    th: { borderBottomWidth: 0 },
                },
            },
        },
    };
});

const variants = {
    simple: variantSimple,
    striped: variantStripe,
    unstyled: defineStyle({}),
};

const sizes: Record<string, PartsStyleObject<typeof parts>> = {
    sm: definePartsStyle({
        th: {
            px: '4',
            py: '1',
            lineHeight: '4',
            fontSize: 'xs',
        },
        td: {
            px: '4',
            py: '4',
            fontSize: 'sm',
            lineHeight: '4',
        },
        caption: {
            px: '4',
            py: '2',
            fontSize: 'xs',
        },
    }),
    md: definePartsStyle({
        th: {
            px: '5',
            py: '3',
            lineHeight: '4',
            fontSize: 'xs',
        },
        td: {
            px: '5',
            py: '4',
            lineHeight: '5',
            fontSize: 'md',
        },
        caption: {
            px: '5',
            py: '2',
            fontSize: 'sm',
        },
    }),
    lg: definePartsStyle({
        th: {
            px: '8',
            py: '4',
            lineHeight: '5',
            fontSize: 'sm',
        },
        td: {
            px: '8',
            py: '7',
            lineHeight: '6',
            fontSize: 'lg',
        },
        caption: {
            px: '6',
            py: '2',
            fontSize: 'md',
        },
    }),
};

const table = defineMultiStyleConfig({
    baseStyle,
    variants,
    sizes,
    defaultProps: {
        variant: 'simple',
        size: 'md',
        colorScheme: 'gray',
    },
});

export default table;
